import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, Lock, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

function formatEroare(detail) {
  if (detail == null) return "A apărut o eroare. Încearcă din nou.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function ResetPassword() {
  const { resetPassword } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [gata, setGata] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirm) {
      toast.error("Parolele nu coincid");
      return;
    }
    setBusy(true);
    try {
      await resetPassword(token, password);
      setGata(true);
    } catch (err) {
      toast.error(formatEroare(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-8 text-center">
        <p className="text-slate-600 font-semibold">Link invalid.</p>
        <button
          data-testid="reset-go-login"
          onClick={() => navigate("/login")}
          className="mt-4 text-emerald-600 font-bold"
        >
          Mergi la autentificare
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col justify-center px-8 py-10 bg-background">
      <div className="w-full max-w-xs mx-auto">
        {gata ? (
          <div className="flex flex-col items-center text-center" data-testid="reset-success">
            <CheckCircle2 className="h-14 w-14 text-emerald-500 mb-4" />
            <h1 className="font-heading text-2xl font-black text-slate-900">
              Parolă schimbată!
            </h1>
            <p className="text-slate-500 text-sm mt-2">
              Acum te poți autentifica cu noua parolă.
            </p>
            <button
              data-testid="reset-go-login"
              onClick={() => navigate("/login")}
              className="mt-6 w-full h-12 rounded-2xl bg-emerald-600 text-white font-bold hover:bg-emerald-700 active:scale-95 transition-transform"
            >
              Mergi la autentificare
            </button>
          </div>
        ) : (
          <>
            <div className="text-center mb-8">
              <div className="h-20 w-20 rounded-3xl bg-emerald-100 flex items-center justify-center mb-5 overflow-hidden shadow-sm mx-auto">
                <img src="/chit-icon.png" alt="Chit" className="h-full w-full object-cover" />
              </div>
              <h1 className="font-heading text-3xl font-black text-slate-900">
                Parolă nouă
              </h1>
              <p className="text-slate-500 text-sm mt-2">
                Alege o parolă nouă pentru contul tău.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-3">
              <div className="relative">
                <Lock className="h-4 w-4 text-slate-400 absolute left-4 top-1/2 -translate-y-1/2" />
                <input
                  data-testid="reset-password-input"
                  type="password"
                  placeholder="Parolă nouă"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={6}
                  className="w-full h-12 rounded-xl border border-slate-200 pl-11 pr-4 text-sm outline-none focus:border-emerald-500"
                />
              </div>
              <div className="relative">
                <Lock className="h-4 w-4 text-slate-400 absolute left-4 top-1/2 -translate-y-1/2" />
                <input
                  data-testid="reset-confirm-input"
                  type="password"
                  placeholder="Confirmă parola"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  required
                  minLength={6}
                  className="w-full h-12 rounded-xl border border-slate-200 pl-11 pr-4 text-sm outline-none focus:border-emerald-500"
                />
              </div>
              <button
                data-testid="reset-submit-btn"
                type="submit"
                disabled={busy}
                className="w-full h-12 rounded-2xl bg-emerald-600 text-white font-bold flex items-center justify-center gap-2 hover:bg-emerald-700 active:scale-95 transition-transform disabled:opacity-60"
              >
                {busy && <Loader2 className="h-4 w-4 animate-spin" />}
                Schimbă parola
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
